/**
 * client/src/characters/CharacterCard.tsx — one person, their lines, and what giving them a voice costs.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE THREE SAMPLE SENTENCES ARE THE POINT OF THIS CARD.
 *
 * A line count is a number somebody has to trust. Three real sentences, verbatim, with their chapter,
 * are a claim they can check in four seconds — and checking is the entire job of this screen, because
 * the attribution behind it is a guess with a stated accuracy. `structure/EvidenceRail.tsx` made the
 * same decision for chapter openings for the same reason: *a gate that shows a verdict and hides the
 * reasoning gives a human nothing to disagree with.*
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Choosing a voice does not spend anything.** It fetches a price. The button that spends says
 * what it will spend, in the sentence the server computed — never a number this file worked out for
 * itself, because a cost estimated by the client and a cost paid by the server are two
 * implementations of one rule and the case they disagree on is the one nobody screenshots.
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`.
 */

import { useCallback, useState, type ReactElement } from "react";

import { ApiError, api } from "../lib/api.ts";
import type { CastCharacter, CastQuote, CastScreen, CastVoice } from "./wire.ts";

interface PatchAnswer {
  applied: boolean;
  quote: CastQuote | null;
}

export function CharacterCard({
  bookId,
  character,
  voices,
  narratorLabel,
  onChanged,
}: {
  bookId: string;
  character: CastCharacter;
  voices: CastVoice[];
  narratorLabel: string;
  onChanged: (screen: CastScreen) => void;
}): ReactElement {
  const [chosen, setChosen] = useState<string | null>(character.voiceId);
  const [quote, setQuote] = useState<CastQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(character.name);

  const base = `/books/${encodeURIComponent(bookId)}/characters/${encodeURIComponent(character.id)}`;

  /** Price on selection. Nothing is written; the answer carries the sentence the button will show. */
  const price = useCallback(
    async (voiceId: string | null) => {
      setChosen(voiceId);
      setError(null);
      if (voiceId === character.voiceId) {
        setQuote(null);
        return;
      }
      setBusy(true);
      try {
        const got = await api.patch<PatchAnswer>(base, { voiceId, apply: false });
        setQuote(got.quote);
      } catch (err) {
        // The refusals here are named ones — `voice-unavailable` and `cast-split` — and both carry a
        // sentence a person can act on. Rendering `err.message` verbatim is deliberate: "that voice
        // cannot be used" sends somebody to restart a machine that was never the problem.
        setError(err instanceof ApiError ? err.message : "Couldn't work out what that would cost.");
        setQuote(null);
      } finally {
        setBusy(false);
      }
    },
    [base, character.voiceId],
  );

  const applyVoice = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const got = await api.patch<PatchAnswer & CastScreen>(base, { voiceId: chosen, apply: true });
      setQuote(got.quote);
      onChanged(got);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change that voice.");
    } finally {
      setBusy(false);
    }
  }, [base, chosen, onChanged]);

  const rename = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed === "" || trimmed === character.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      const got = await api.patch<PatchAnswer & CastScreen>(base, { name: trimmed });
      onChanged(got);
      setRenaming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't rename.");
    } finally {
      setBusy(false);
    }
  }, [base, character.name, name, onChanged]);

  const recolour = useCallback(
    async (color: string) => {
      setBusy(true);
      try {
        const got = await api.patch<PatchAnswer & CastScreen>(base, { color });
        onChanged(got);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Couldn't change that colour.");
      } finally {
        setBusy(false);
      }
    },
    [base, onChanged],
  );

  const changed = chosen !== character.voiceId;

  return (
    <article className="ab-card">
      <div className="ab-card-body">
        <div className="ab-book-head">
          {/* The cloth-board swatch. It is a BUTTON, because recolouring is how two characters with
              similar names stop being confused for one another, and burying that in a menu makes it
              a thing nobody does. */}
          <button
            type="button"
            className={`ab-mark ${character.color === null ? "" : `ab-${character.color}`}`}
            aria-label={`Change ${character.name}'s colour`}
            disabled={busy}
            onClick={() => void recolour(nextColour(character.color))}
          />
          {renaming ? (
            <input
              className="ab-field"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onBlur={() => void rename()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void rename();
                if (e.key === "Escape") {
                  setName(character.name);
                  setRenaming(false);
                }
              }}
            />
          ) : (
            <button type="button" className="ab-link ab-h3" onClick={() => setRenaming(true)}>
              {character.name}
            </button>
          )}
          {character.isNarrator && <span className="ab-chip ab-chip-quiet">the narrator</span>}
        </div>

        {character.aliases.length > 0 && (
          <p className="ab-hint ab-hint-quiet">also {character.aliases.join(" · ")}</p>
        )}

        {/* The counts, and the share of what is SPOKEN — labelled, because a share of the whole book
            would be a different and much smaller number and nobody could tell which they were seeing. */}
        <p className="ab-card-meta">
          <span className="ab-num">{character.lines.toLocaleString("en-GB")}</span> lines ·{" "}
          <span className="ab-num">{character.chars.toLocaleString("en-GB")}</span> characters ·{" "}
          {(character.share * 100).toFixed(0)}% of what is spoken
        </p>

        {/* Where the attribution came from, per character, so a human knows which cards to read. A
            card that is 100% explicit tags is one nobody needs to check. */}
        <p className="ab-hint">{splitOf(character)}</p>

        {character.samples.length === 0 ? (
          <p className="ab-hint ab-hint-quiet">No lines are assigned to this character yet.</p>
        ) : (
          <ul className="ab-runners">
            {character.samples.map((s) => (
              <li key={s.sid} className="ab-runner-title">
                <span className="ab-sid">ch. {s.chapterOrd}</span> {s.text}
              </li>
            ))}
          </ul>
        )}

        {/* ── the voice ──────────────────────────────────────────────────── */}
        <div className="ab-field">
          <label className="ab-eyebrow" htmlFor={`voice-${character.id}`}>
            Read by
          </label>
          <select
            id={`voice-${character.id}`}
            className="ab-select"
            value={chosen ?? ""}
            disabled={busy}
            onChange={(e) => void price(e.target.value === "" ? null : e.target.value)}
          >
            {/* Empty string is NULL — "read by the narrator" — and it is first because it is the
                answer for almost every character in almost every book. */}
            <option value="">the narrator ({narratorLabel})</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
                {v.kind === "base" ? " (stock voice)" : ""}
              </option>
            ))}
          </select>
        </div>

        {error !== null && <p className="ab-hint ab-hint-bad">{error}</p>}

        {changed && quote !== null && (
          <div className="ab-confirm-quote">
            {/* VERBATIM from the server. The last clause of it — "nothing the narrator says is
                re-rendered" — is the sentence this whole design exists to be able to say truthfully. */}
            <p className="ab-note">{quote.summary}</p>
            {quote.cached > 0 && (
              <p className="ab-hint ab-hint-quiet">
                {quote.cached.toLocaleString("en-GB")} of those lines have already been said in this voice and cost nothing.
              </p>
            )}
            <p className="ab-hint ab-hint-quiet">
              Nothing is queued. The pages simply need the voice; a chapter, a whole-book run, or pressing play is what makes them.
            </p>
            <div className="ab-actions">
              <button type="button" className="ab-btn ab-btn-primary" disabled={busy} onClick={() => void applyVoice()}>
                {quote.free ? "Use this voice — costs nothing" : `Use this voice — about ${quote.estGpuMin || "<1"} min of spark-1`}
              </button>
              <button type="button" className="ab-btn ab-btn-ghost" disabled={busy} onClick={() => void price(character.voiceId)}>
                Keep {character.voiceLabel ?? "the narrator"}
              </button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

/** `318 from an explicit tag · 94 from the reader model · 41 from you`, with the zeroes left out. */
function splitOf(c: CastCharacter): string {
  const parts: string[] = [];
  if (c.confidence.rule > 0) parts.push(`${c.confidence.rule.toLocaleString("en-GB")} from an explicit tag`);
  if (c.confidence.llm > 0) parts.push(`${c.confidence.llm.toLocaleString("en-GB")} from the reader model`);
  if (c.confidence.human > 0) parts.push(`${c.confidence.human.toLocaleString("en-GB")} you assigned`);
  return parts.length === 0 ? "Nothing has been attributed to this character." : parts.join(" · ");
}

/**
 * The eight cloth boards, cycling. Names, not hexes — `--ab-board-1..8` in `tokens.css`, which carry
 * their own note about not changing with the theme. That property is exactly what a "which one is
 * Holmes" swatch needs.
 */
const BOARDS = ["board-1", "board-2", "board-3", "board-4", "board-5", "board-6", "board-7", "board-8"];

function nextColour(current: string | null): string {
  const at = current === null ? -1 : BOARDS.indexOf(current);
  return BOARDS[(at + 1) % BOARDS.length]!;
}
